import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
    MdSearch, MdEdit, MdDelete, MdPersonAdd, MdCheckCircle,
    MdBlock, MdPhone, MdEmail, MdRefresh, MdWarning, MdPerson
} from "react-icons/md";

// API
import { api, usersAPI } from "services/api";

// İleride yazacağımız modalların importları (Şimdilik boş componentler dönebilirler)
import StaffAddModal from "./components/StaffAddModal";
import StaffEditModal from "./components/StaffEditModal";

const PAGE_SIZE = 7;

// --- SKELETON LOADER ---
const StaffSkeleton = () => (
    <div className="flex w-full flex-col gap-4 animate-pulse mt-5">
        {/* Üst Sekmeler ve Arama İskeleti */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="h-12 w-full md:w-64 rounded-xl bg-gray-200 dark:bg-navy-700" />
            <div className="h-12 w-full rounded-xl bg-gray-200 dark:bg-navy-700" />
        </div>
        {/* Tablo/Kart İskeleti */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="h-32 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
            ))}
        </div>
    </div>
);

const AdminStaffList = () => {
    // --- STATELER (Staff.js'den Birebir Aktarım) ---
    const [activeTab, setActiveTab] = useState('active'); // 'active' | 'passive'
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [actionLoading, setActionLoading] = useState(null); // Buton loader için

    // Modal Stateleri
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingStaffId, setEditingStaffId] = useState(null);

    // --- API FETCH (Staff.js Work-Logic) ---
    const fetchStaffData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Tab'a göre CoreUI'daki birebir aynı endpoint seçimi
            const endpoint = activeTab === 'active' ? '/staff' : '/users/passive-staff';
            const response = await api.get(endpoint);

            let rawData = [];
            if (activeTab === 'active') {
                rawData = response.staff || [];
            } else {
                rawData = response.passive_staff || [];
            }

            // Datayı formatlama
            const formattedStaff = rawData.map((person) => ({
                id: person.id,
                firstName: person.name,
                lastName: person.surname,
                role: "staff",
                phone: person.phone,
                email: person.email,
                active: person.is_active !== 0,
                joinDate: person.created_at ? new Date(person.created_at).toISOString().split("T")[0] : "-",
                tag: person.tag || null,
            }));

            setStaffList(formattedStaff);
        } catch (err) {
            console.error("Personel verileri yüklenirken hata:", err);
            setError(err.message || "Veriler alınamadı");
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    // Tab değiştiğinde veriyi yeniden çek, aramayı ve sayfayı sıfırla
    useEffect(() => {
        setQ("");
        setPage(1);
        fetchStaffData();
    }, [fetchStaffData]);

    // --- FİLTRELEME ---
    const filtered = useMemo(() => {
        if (!staffList?.length) return [];
        const query = q.toLowerCase();
        return staffList.filter((s) => {
            const fullName = `${s.firstName || ""} ${s.lastName || ""}`.toLowerCase();
            return !q || fullName.includes(query) || (s.email && s.email.toLowerCase().includes(query));
        });
    }, [staffList, q]);

    // --- SAYFALAMA (PAGINATION) ---
    const totalFilteredCount = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalFilteredCount / PAGE_SIZE));
    const startIdx = (page - 1) * PAGE_SIZE;
    const endIdx = Math.min(startIdx + PAGE_SIZE, totalFilteredCount);
    const pageItems = filtered.slice(startIdx, endIdx);

    // --- AKSİYONLAR (Work-Logic) ---

    // Pasife Alma (Soft Delete)
    const handleDeactivate = async (id) => {
        if (!window.confirm("Bu personeli pasife almak istediğinize emin misiniz? Sistemden tamamen silinmeyecek, ancak giriş yapamayacak.")) return;
        setActionLoading(`deactivate-${id}`);
        try {
            const res = await usersAPI.delete(id);
            if (res.success || res) {
                setStaffList((prev) => prev.filter((s) => s.id !== id));
                // alert("Personel pasife alındı."); // Toast vs eklenebilir, şimdilik coreui mantığı
            }
        } catch (err) {
            alert("İşlem başarısız: " + (err.message || "Bilinmeyen hata"));
        } finally {
            setActionLoading(null);
        }
    };

    // Aktifleştirme (Restore)
    const handleActivate = async (id) => {
        if (!window.confirm("Bu personeli tekrar aktif etmek istediğinize emin misiniz?")) return;
        setActionLoading(`activate-${id}`);
        try {
            const res = await api.post(`/users/${id}/activate`);
            if (res.success || res) {
                setStaffList((prev) => prev.filter((s) => s.id !== id));
                // alert("Personel tekrar aktif edildi.");
            }
        } catch (err) {
            alert("İşlem başarısız: " + (err.message || "Bilinmeyen hata"));
        } finally {
            setActionLoading(null);
        }
    };

    // Yardımcılar
    const getInitials = (firstName, lastName) => `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
    const displayPhone = (p) => p ? (p.startsWith("+") ? p : `0${p}`) : "-";

    // --- RENDER ---
    if (loading) return <StaffSkeleton />;

    return (
        <div className="mt-5 flex w-full flex-col gap-6">

            {/* 1. ÜST BİLGİ VE AKSİYON BAR */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 rounded-[20px] bg-white p-6 shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div>
                    <h2 className="text-2xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
                        <MdPerson className="text-brand-500" /> Personel Yönetimi
                    </h2>
                    <p className="mt-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                        İşletme personellerini ve hesap durumlarını yönetin.
                    </p>
                </div>
                <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-brand-600 active:scale-95 shadow-lg shadow-brand-500/30"
                >
                    <MdPersonAdd className="text-xl" /> Yeni Personel
                </button>
            </div>

            {error && (
                <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl text-red-600 dark:text-red-400">
                    <div className="flex items-center gap-2 font-bold"><MdWarning /> {error}</div>
                    <button onClick={fetchStaffData} className="text-sm font-bold underline">Tekrar Dene</button>
                </div>
            )}

            {/* 2. ANA KART (TABLAR, ARAMA, LİSTE) */}
            <div className="flex flex-col bg-white dark:bg-navy-800 rounded-[20px] shadow-3xl shadow-shadow-500 dark:shadow-none border border-gray-100 dark:border-white/10 overflow-hidden">

                {/* TABS & SEARCH KONTROLLERİ */}
                <div className="flex flex-col lg:flex-row items-center justify-between border-b border-gray-100 dark:border-white/10 p-4 gap-4 bg-gray-50/50 dark:bg-navy-900/20">

                    {/* Tabs (HorizonUI Pill Style) */}
                    <div className="flex w-full lg:w-fit bg-gray-100 dark:bg-navy-900 rounded-xl p-1.5">
                        <button
                            onClick={() => setActiveTab('active')}
                            className={`flex-1 lg:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${
                                activeTab === 'active'
                                    ? 'bg-white text-brand-500 shadow-md dark:bg-navy-800 dark:text-brand-400'
                                    : 'text-gray-500 hover:text-navy-700 dark:hover:text-white'
                            }`}
                        >
                            <MdCheckCircle className="text-lg" /> Aktif Personeller
                        </button>
                        <button
                            onClick={() => setActiveTab('passive')}
                            className={`flex-1 lg:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${
                                activeTab === 'passive'
                                    ? 'bg-white text-red-500 shadow-md dark:bg-navy-800 dark:text-red-400'
                                    : 'text-gray-500 hover:text-navy-700 dark:hover:text-white'
                            }`}
                        >
                            <MdBlock className="text-lg" /> Pasif / Arşiv
                        </button>
                    </div>

                    {/* Search */}
                    <div className="relative w-full lg:w-80">
                        <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                        <input
                            type="text"
                            placeholder={`${activeTab === 'active' ? 'Aktif' : 'Pasif'} personel ara...`}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="w-full h-11 pl-10 pr-4 rounded-xl border border-gray-200 bg-white text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:focus:border-brand-400 transition-all"
                        />
                    </div>
                </div>

                {/* İÇERİK BÖLÜMÜ */}
                <div className="p-4 sm:p-6">
                    {filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-14 opacity-60">
                            {activeTab === 'active' ? <MdPerson className="text-6xl text-gray-400 mb-4" /> : <MdBlock className="text-6xl text-gray-400 mb-4" />}
                            <h5 className="text-xl font-bold text-navy-700 dark:text-white">Kayıt Bulunamadı</h5>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-1">Bu kategoride listelenecek personel yok.</p>
                        </div>
                    ) : (
                        <>
                            {/* === MOBİL GÖRÜNÜM (KARTLAR) === */}
                            <div className="grid grid-cols-1 md:hidden gap-4">
                                {pageItems.map((s) => (
                                    <div key={s.id} className={`relative flex flex-col rounded-2xl border p-5 shadow-sm transition-all ${activeTab === 'passive' ? 'border-red-100 bg-red-50/30 dark:border-red-500/10 dark:bg-red-500/5' : 'border-gray-100 bg-white dark:border-white/5 dark:bg-navy-800'}`}>

                                        {/* Status Ribbon */}
                                        <div className={`absolute left-0 top-0 h-full w-1.5 rounded-l-2xl ${activeTab === 'passive' ? 'bg-red-500' : 'bg-brand-500'}`} />

                                        <div className="flex items-center justify-between mb-4 pl-2">
                                            <div className="flex items-center gap-3">
                                                <div className={`flex h-12 w-12 items-center justify-center rounded-full font-bold text-white shadow-md ${activeTab === 'passive' ? 'bg-gray-400 grayscale' : 'bg-brand-500'}`}>
                                                    {getInitials(s.firstName, s.lastName)}
                                                </div>
                                                <div>
                                                    <h4 className={`text-base font-bold ${activeTab === 'passive' ? 'text-gray-500 dark:text-gray-400' : 'text-navy-700 dark:text-white'}`}>
                                                        {s.firstName} {s.lastName}
                                                    </h4>
                                                    {s.tag && <span className="mt-0.5 inline-block rounded bg-brand-50 px-2 py-0.5 text-[10px] font-bold text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">{s.tag}</span>}
                                                </div>
                                            </div>
                                            {activeTab === 'passive' && <span className="rounded-md bg-red-100 px-2 py-1 text-[10px] font-bold text-red-600 dark:bg-red-500/20 dark:text-red-400">PASİF</span>}
                                        </div>

                                        <div className="flex flex-col gap-2 pl-2 mb-5">
                                            <div className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                                                <MdPhone className="text-gray-400" /> {displayPhone(s.phone)}
                                            </div>
                                            <div className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                                                <MdEmail className="text-gray-400" /> {s.email || '-'}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 pl-2 pt-4 border-t border-gray-100 dark:border-white/10">
                                            {activeTab === 'active' ? (
                                                <>
                                                    <button onClick={() => setEditingStaffId(s.id)} className="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl bg-gray-50 text-sm font-bold text-navy-700 hover:bg-gray-100 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-700 transition-all">
                                                        <MdEdit /> Düzenle
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeactivate(s.id)}
                                                        disabled={actionLoading === `deactivate-${s.id}`}
                                                        className="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl bg-red-50 text-sm font-bold text-red-600 hover:bg-red-500 hover:text-white dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500 dark:hover:text-white transition-all disabled:opacity-50"
                                                    >
                                                        {actionLoading === `deactivate-${s.id}` ? <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" /> : <><MdBlock /> Pasife Al</>}
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={() => handleActivate(s.id)}
                                                    disabled={actionLoading === `activate-${s.id}`}
                                                    className="w-full flex items-center justify-center gap-2 h-10 rounded-xl bg-green-50 text-sm font-bold text-green-600 hover:bg-green-500 hover:text-white dark:bg-green-500/10 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white transition-all disabled:opacity-50"
                                                >
                                                    {actionLoading === `activate-${s.id}` ? <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" /> : <><MdRefresh className="text-lg" /> Tekrar Aktif Et</>}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* === MASAÜSTÜ GÖRÜNÜM (TABLO) === */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                    <tr className="border-b border-gray-200 dark:border-white/10 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                                        <th className="py-4 pl-4">Personel</th>
                                        <th className="py-4">İletişim</th>
                                        <th className="py-4">Katılım Tarihi</th>
                                        <th className="py-4 pr-4 text-center">İşlemler</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {pageItems.map((s) => (
                                        <tr key={s.id} className="border-b border-gray-50 dark:border-white/5 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors">
                                            <td className="py-4 pl-4">
                                                <div className="flex items-center gap-3">
                                                    <div className={`flex h-10 w-10 items-center justify-center rounded-full font-bold text-white shadow-md ${activeTab === 'passive' ? 'bg-gray-400 grayscale' : 'bg-brand-500'}`}>
                                                        {getInitials(s.firstName, s.lastName)}
                                                    </div>
                                                    <div>
                                                        <p className={`text-sm font-bold ${activeTab === 'passive' ? 'text-gray-500 dark:text-gray-400' : 'text-navy-700 dark:text-white'}`}>
                                                            {s.firstName} {s.lastName}
                                                        </p>
                                                        {s.tag && <span className="mt-0.5 inline-block rounded bg-brand-50 px-2 py-0.5 text-[10px] font-bold text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">{s.tag}</span>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4">
                                                <div className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400"><MdPhone /> {displayPhone(s.phone)}</div>
                                                <div className="text-xs text-gray-400 mt-1">{s.email || '-'}</div>
                                            </td>
                                            <td className="py-4">
                                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{s.joinDate}</span>
                                            </td>
                                            <td className="py-4 pr-4">
                                                <div className="flex items-center justify-center gap-2">
                                                    {activeTab === 'active' ? (
                                                        <>
                                                            <button onClick={() => setEditingStaffId(s.id)} title="Düzenle" className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50 text-gray-500 hover:bg-brand-50 hover:text-brand-500 dark:bg-navy-900 dark:hover:bg-brand-400/10 dark:hover:text-brand-400 transition-all">
                                                                <MdEdit className="text-lg" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeactivate(s.id)}
                                                                disabled={actionLoading === `deactivate-${s.id}`}
                                                                title="Pasife Al"
                                                                className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:bg-navy-900 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-all disabled:opacity-50"
                                                            >
                                                                {actionLoading === `deactivate-${s.id}` ? <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" /> : <MdBlock className="text-lg" />}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleActivate(s.id)}
                                                            disabled={actionLoading === `activate-${s.id}`}
                                                            title="Geri Getir"
                                                            className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50 text-gray-500 hover:bg-green-50 hover:text-green-600 dark:bg-navy-900 dark:hover:bg-green-500/10 dark:hover:text-green-400 transition-all disabled:opacity-50"
                                                        >
                                                            {actionLoading === `activate-${s.id}` ? <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" /> : <MdRefresh className="text-lg" />}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {/* === SAYFALAMA (PAGINATION) === */}
                    {totalPages > 1 && (
                        <div className="mt-6 flex items-center justify-between border-t border-gray-100 dark:border-white/10 pt-4">
                            <p className="text-sm font-medium text-gray-500">
                                Toplam <span className="font-bold text-navy-700 dark:text-white">{totalFilteredCount}</span> kayıttan <span className="font-bold text-navy-700 dark:text-white">{startIdx + 1}-{endIdx}</span> arası.
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="flex h-8 px-3 items-center justify-center rounded-lg border border-gray-200 bg-white text-sm font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700 transition-all"
                                >
                                    Geri
                                </button>
                                <div className="flex h-8 min-w-[32px] items-center justify-center rounded-lg bg-brand-500 px-2 text-sm font-bold text-white shadow-md">
                                    {page}
                                </div>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="flex h-8 px-3 items-center justify-center rounded-lg border border-gray-200 bg-white text-sm font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700 transition-all"
                                >
                                    İleri
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* MODALLAR BURAYA GELECEK (Adım 2 ve Adım 3'te içleri dolacak) */}
            {isAddModalOpen && <StaffAddModal isOpen={isAddModalOpen} onClose={() => {setIsAddModalOpen(false); fetchStaffData();}} />}
            {editingStaffId && <StaffEditModal staffId={editingStaffId} onClose={() => {setEditingStaffId(null); fetchStaffData();}} />}

        </div>
    );
};

export default AdminStaffList;